// ---- Wire events from the SSE stream (shapes verified against the service) ----
export interface SessionCompletePayload {
  transitOperationId: number
  validation: unknown
  /**
   * NULL whenever the turn produced no declaration. The service builds this as
   * `f"{ui}/transit-operation/{tid}" if tid > 0 else None` (`session_manager._finish`), so an
   * all-`status` turn — a remit refusal, a question, a document warning — sends `null` on the wire.
   * It was typed `string` and every fixture used `''`, so the one shape the service actually sends
   * for a refused turn was untyped and untested.
   */
  openUrl: string | null
}

/**
 * What the service says a tool step WAS (T1-3894/T1-3895). The service never sends a method or a path
 * any more, for any role: `summary` is a neutral label and these fields carry the meaning. All absent
 * on an older service's frames, which is what the fallbacks in `util/activity.ts` and `util/drafts.ts`
 * key on. Validated into `ToolActivity` by `toActivity` before it reaches state.
 */
export interface ToolActivityWire {
  activity?: string
  step?: string
  methodClass?: string
  document?: string
  declarationId?: number
}

export type SseEvent =
  | { event: 'status'; data: { text: string } }
  | { event: 'tool_use'; data: { summary: string } & ToolActivityWire }
  | { event: 'tool_result'; data: { ok: boolean; summary: string } & ToolActivityWire }
  | { event: 'tool_call_confirmation'; data: { confirmationId: string; method: string; path: string; summary: string } }
  | { event: 'question'; data: { text: string } }
  | { event: 'confidence'; data: { reading: string; draft: string; flagged: string[] } }
  | { event: 'session_complete'; data: SessionCompletePayload }
  | { event: 'error'; data: { message: string } }

// ---- Reducer state ----
export type Phase =
  | 'empty' | 'uploading' | 'creating' | 'queued' | 'streaming'
  | 'awaiting_confirmation' | 'awaiting_answer'
  | 'complete' | 'error' | 'interrupted'

/**
 * A tool step as the panel understands it. Not named `kind`: that is `TimelineItem`'s discriminant, and
 * a wire field of that name spread onto the item would turn it into something no branch renders.
 */
export type ToolActivity =
  | { type: 'api'; step: string; methodClass: 'read' | 'write'; declarationId?: number }
  | { type: 'read'; document?: string }

export type TimelineItem =
  | { id: string; kind: 'status'; at: number; text: string }
  | {
    id: string
    kind: 'tool'
    at: number
    summary: string
    status: 'running' | 'ok' | 'failed'
    /** Absent for an older service's frame and for the client's own trace marker. */
    activity?: ToolActivity
  }
  | { id: string; kind: 'error'; at: number; message: string; cancelled: boolean }
  | { id: string; kind: 'user'; at: number; text: string; files: string[] }

export interface SessionState {
  clientId: string // stable registry key, assigned before the server sessionId exists
  sessionId: string | null
  title: string
  phase: Phase
  timeline: TimelineItem[]
  pendingConfirmation?: { confirmationId: string; method: string; path: string; summary: string }
  pendingQuestion?: { text: string; terminal: boolean }
  confidence?: { reading: string; draft: string; flagged: string[] }
  result?: SessionCompletePayload
  interruptRequested: boolean
  unread: boolean
  error?: string
}

export interface CopilotState {
  open: boolean
  activeId: string | null // clientId of the visible session
  sessions: Record<string, SessionState> // keyed by clientId
  order: string[] // clientIds, tab order
}

// ---- Actions ----
export type CopilotAction =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'NEW_SESSION'; clientId: string }
  | { type: 'SET_ACTIVE'; clientId: string }
  | { type: 'UPLOAD_START'; clientId: string; title: string }
  | { type: 'SESSION_CREATING'; clientId: string }
  | { type: 'SESSION_CREATED'; clientId: string; sessionId: string }
  | { type: 'MESSAGE_TRACED'; clientId: string; traceId: string | null }
  | { type: 'MESSAGE_SENT'; clientId: string }
  | { type: 'CONFIRM_SENT'; clientId: string }
  | { type: 'INTERRUPT_REQUESTED'; clientId: string }
  | { type: 'USER_TURN'; clientId: string; at: number; text: string; files: string[] }
  // `at` is the dispatcher's clock. Optional so the reducer stays pure and callers that
  // don't care about ordering (tests) can omit it; absent means "no timestamp" (0).
  | { type: 'SSE'; clientId: string; event: SseEvent; at?: number }

/** Substring identifying the service's terminal run-cap nudge (a `question` that cannot be answered). */
export const RUN_CAP_HINT = 'continue in the portal'

/** Prefix of the admin-only `tool` item that records a turn's message trace id and session id. */
export const TRACE_MARKER = 'trace: '

/** Phases a run does not come back from without a fresh USER_TURN. */
export const TERMINAL_PHASES: Phase[] = ['complete', 'error', 'interrupted']
